import { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { Box, Typography, Alert, Chip } from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import jschardet from 'jschardet';

function MailingListUpload({ buttons, file, setFile, setNextDisabled, setColumns, setEncoding }) {
    const { t } = useTranslation();
    const [error, setError] = useState(null);

    useEffect(() => {
        setNextDisabled(!file);
    }, [file, setNextDisabled]);

    async function detectFileEncoding(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = function(event) {
                const textContent = event.target.result;
                const detectedEncoding = jschardet.detect(textContent).encoding || 'UTF-8';
                resolve(detectedEncoding);
            };
            reader.onerror = function() {
                reject(new Error("Impossible de lire le fichier"));
            };
            // Limiter la taille à 100 Ko pour la détection
            const blob = file.slice(0, 100 * 1024);
            reader.readAsBinaryString(blob);
        });
    }

    const onDrop = useCallback((acceptedFiles) => {
        setFile(acceptedFiles[0]);
        detectFileEncoding(acceptedFiles[0]).then((encoding) => {
            setEncoding(encoding);
        });
        setError(null);
        setColumns([]);
    }, [setFile, setError, setColumns, setEncoding]);

    const onDropRejected = useCallback((fileRejections) => {
        // console.log(fileRejections[0].errors[0].message);
        setError(t('The selected file is not a valid CSV file. Please upload a file in CSV format.'));
    }, [t]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        onDropRejected,
        multiple: false,
        accept: {
            'text/csv': ['.csv'],
        },
    });

    return (
        <div>
            <Box sx={{ mb: 5 }}>

                {file ? (
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography>{t('File selected')}:</Typography>
                        <Chip icon={<DescriptionIcon />} label={file.name} variant="outlined" onDelete={() => setFile(null)} />
                    </Box>
                ) : (
                    <Box
                        {...getRootProps()}
                        sx={{
                            border: "2px dashed #ccc",
                            borderRadius: "8px",
                            padding: "16px",
                            textAlign: "center",
                            cursor: "pointer",
                        }}
                    >
                        <input {...getInputProps()} />
                        {isDragActive ? (
                            <Typography>{t('Drop the file here ...')}</Typography>
                        ) : (
                            <Typography>{t('Drag a CSV file here, or click to select a file.')}</Typography>
                        )}
                    </Box>
                )}

                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
                )}

                <Alert severity="info" sx={{ mt: 2 }}>
                    {t('Choose a file containing a list of recipients.')}&nbsp;
                    {t('The file must be in CSV format; the application will automatically detect settings such as the separator or encoding. The first line of the file must be a header row specifying the name of each column.')}&nbsp;
                    {t('The recommended columns are: title, first name, last name, email, phone.')}
                    <br />
                    {t('If provided, the phone number must be in international format (e.g., +33612345678) or optionally in French format (e.g., 06 12 34 56 78).')}
                </Alert>

            </Box>

            {buttons}
        </div>
    );
}

export default MailingListUpload;